import { i18n } from '@quadralize/i18n'
import { sentryCreateBrowserRouter } from '@quadralize/sentry'
import { lazy, Suspense } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { BackofficeLayout } from '@/app/layouts/backoffice-layout/backoffice-layout'
import { ProtectedRoute } from '@/components/protected-route/protected-route'
import { PublicRoute } from '@/components/public-route/public-route'
import { RouteLoader } from '@/components/route-loader/route-loader'
import { GenericError, NotFound } from '@/modules/error'

const Dashboard = lazy(() => import('@/modules/dashboard').then((m) => ({ default: m.Dashboard })))
const CompanyList = lazy(() =>
  import('@/modules/companies').then((m) => ({ default: m.CompanyList })),
)
const CompanyProfile = lazy(() =>
  import('@/modules/companies').then((m) => ({ default: m.CompanyProfile })),
)
const OriginList = lazy(() => import('@/modules/origins').then((m) => ({ default: m.OriginList })))
const UserList = lazy(() => import('@/modules/users').then((m) => ({ default: m.UserList })))
const Login = lazy(() => import('@/modules/auth').then((m) => ({ default: m.Login })))
const ForgotPassword = lazy(() =>
  import('@/modules/auth').then((m) => ({ default: m.ForgotPassword })),
)
const VerifyCode = lazy(() => import('@/modules/auth').then((m) => ({ default: m.VerifyCode })))
const ResetPassword = lazy(() =>
  import('@/modules/auth').then((m) => ({ default: m.ResetPassword })),
)
const ActivateAccount = lazy(() =>
  import('@/modules/auth').then((m) => ({ default: m.ActivateAccount })),
)

function getLayoutTranslation(key: string): string {
  return i18n.t(key)
}

function ActivateAccountRedirect() {
  const [searchParams] = useSearchParams()
  return <Navigate to={`/ativar-conta?${searchParams.toString()}`} replace />
}

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <BackofficeLayout />
      </ProtectedRoute>
    ),
    errorElement: <GenericError />,
    children: [
      {
        index: true,
        handle: { breadcrumb: () => getLayoutTranslation('layout:navigation.dashboard') },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: 'empresas',
        handle: { breadcrumb: () => getLayoutTranslation('layout:navigation.companies') },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <CompanyList />
          </Suspense>
        ),
      },
      {
        path: 'empresas/:companyId',
        handle: {
          breadcrumbs: [
            {
              label: () => getLayoutTranslation('layout:navigation.companies'),
              path: '/empresas',
            },
            { label: () => getLayoutTranslation('layout:navigation.companyProfile') },
          ],
        },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <CompanyProfile />
          </Suspense>
        ),
      },
      {
        path: 'origens',
        handle: { breadcrumb: () => getLayoutTranslation('layout:navigation.origins') },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <OriginList />
          </Suspense>
        ),
      },
      {
        path: 'usuarios',
        handle: { breadcrumb: () => getLayoutTranslation('layout:navigation.users') },
        element: (
          <Suspense fallback={<RouteLoader />}>
            <UserList />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/entrar',
    element: (
      <PublicRoute>
        <Suspense fallback={<RouteLoader />}>
          <Login />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/esqueci-senha',
    element: (
      <PublicRoute>
        <Suspense fallback={<RouteLoader />}>
          <ForgotPassword />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/verificar-codigo',
    element: (
      <PublicRoute>
        <Suspense fallback={<RouteLoader />}>
          <VerifyCode />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/redefinir-senha',
    element: (
      <PublicRoute>
        <Suspense fallback={<RouteLoader />}>
          <ResetPassword />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/ativar-conta',
    element: (
      <Suspense fallback={<RouteLoader />}>
        <ActivateAccount />
      </Suspense>
    ),
  },
  {
    path: '/activate-account',
    element: <ActivateAccountRedirect />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
])
